import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    Image,
    Linking,
    Platform,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { VisitComplianceBlockerModal } from "@/components/visit/VisitComplianceBlockerModal";
import {
    type AvailableVisitDetail,
    type VisitComplianceBlocked,
    extractVisitComplianceBlockers,
    fetchAvailableVisit,
    markAvailableVisitViewed,
    requestVisit
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { logRocketRedactProps } from "@/lib/logrocket";
import { useNotification } from "@/lib/notifications";
import {
    colors,
    fonts,
    gradients,
    radius,
    shadows,
    spacing,
    typography,
    visitTypeColors
} from "@/lib/theme";
import {
    EXACT_ADDRESS_ONCE_YOURS_TEXT,
    allowsDirections,
    approximateStaticMapUrl,
    crossStreetStaticMapUrl,
    distanceAwayText,
    isApproximateLocation,
    isCrossStreetLocation,
    locationChipLabel,
    showsExactAddressHint,
    visitLocationLine
} from "@/lib/visit-location";
import { useWebSocketEvent } from "@/lib/websocket";

/** Map visit type codes to display labels */
const VISIT_TYPE_LABELS: Record<string, string> = {
    SOC: "Start of Care",
    REA: "Reassessment",
    SUP: "Supervisory",
    DC: "Discharge",
    ROC: "Resumption of Care"
};

// ===============================================
// Main Screen
// ===============================================

export default function AvailableVisitDetailScreen() {
    const { id } = useLocalSearchParams<{ id: string }>();
    const router = useRouter();
    const { user } = useAuth();
    //  She can BROWSE the map before activation — that is the point of showing
    //  it — but she cannot claim anything: the server's `assertRnActive` returns
    //  403 on request. Without this the tap would surface that raw internal
    //  message ("RN is not active (status: pending_application)") to a nurse.
    //
    //  Gate on a KNOWN non-active status, never on the absence of one. This
    //  value is cached on the device and was historically only written at
    //  login, so an older session can carry no `rnStatus` at all; treating
    //  "unknown" as "not active" locked fully active nurses out of booking and
    //  sent them to onboarding they had already finished. The server is the
    //  real authority here — being wrong in this direction only costs a 403.
    const notYetActive = user?.rnStatus != null && user.rnStatus !== "active";
    const { showNotification } = useNotification();
    const [visit, setVisit] = useState<AvailableVisitDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [selectedTime, setSelectedTime] = useState<string | null>(null);
    const [showError, setShowError] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    /**
     * When the server's visit-date compliance gate blocks the request,
     * this holds the structured `{ asOfDate, blockers }` payload so the
     * modal can list exactly which items would lapse. Null when there's
     * no active blocker popup.
     */
    const [complianceBlocked, setComplianceBlocked] = useState<VisitComplianceBlocked | null>(null);
    const scrollRef = useRef<ScrollView>(null);
    const timeSlotY = useRef(0);
    const errorShake = useRef(new Animated.Value(0)).current;

    // Fetch visit detail
    useEffect(() => {
        if (!id) {
            return;
        }
        setLoading(true);
        fetchAvailableVisit(Number(id))
            .then(setVisit)
            .catch(() => {
                showNotification("This visit is no longer available", "failed");
                router.back();
            })
            .finally(() => setLoading(false));
    }, [id]);

    // Opening this screen is what "seen" means — it clears the Explore map's
    // "new to you" dot for this visit. Deliberately fire-and-forget and separate
    // from the fetch above: it is a nicety, and a failure must never keep the
    // nurse from reading the visit. The Explore list picks the new state up on
    // its next refresh.
    useEffect(() => {
        if (!id) {
            return;
        }
        markAvailableVisitViewed(Number(id)).catch(() => {
            // Non-fatal: the dot just stays until the next successful open.
        });
    }, [id]);

    // WebSocket: navigate back if this visit is no longer available
    useWebSocketEvent("visit:updated", (data) => {
        if (!id) {
            return;
        }
        const visitId = data.visitId as unknown as number;
        if (visitId !== Number(id)) {
            return; // Not this visit
        }
        // Silently re-fetch to update the UI — don't navigate, user may be on another screen
        fetchAvailableVisit(Number(id))
            .then(setVisit)
            .catch(() => {
                // Visit no longer accessible — ignore, user will see updated Explore when they go back
            });
    });

    const vtColor = visitTypeColors[visit?.visitType ?? "SOC"] ?? visitTypeColors.SOC;

    const selectedDateSlot = useMemo(() => {
        if (!selectedDate || !visit) {
            return null;
        }
        return visit.availableDates.find((d) => d.date === selectedDate) ?? null;
    }, [selectedDate, visit]);

    const handleDateSelect = useCallback(
        (date: string) => {
            setShowError(false);
            if (selectedDate === date) {
                // Untap — deselect date + time
                setSelectedDate(null);
                setSelectedTime(null);
                return;
            }
            setSelectedDate(date);
            setSelectedTime(null);
        },
        [selectedDate]
    );

    const handleTimeSelect = useCallback(
        (time: string) => {
            setShowError(false);
            if (selectedTime === time) {
                // Untap — deselect time
                setSelectedTime(null);
                return;
            }
            setSelectedTime(time);
        },
        [selectedTime]
    );

    const handleSchedulePress = useCallback(() => {
        if (!selectedDate || !selectedTime) {
            setShowError(true);
            // Scroll to the time slot section
            scrollRef.current?.scrollTo({ y: timeSlotY.current - 20, animated: true });
            // Shake animation
            Animated.sequence([
                Animated.timing(errorShake, { toValue: 10, duration: 50, useNativeDriver: true }),
                Animated.timing(errorShake, { toValue: -10, duration: 50, useNativeDriver: true }),
                Animated.timing(errorShake, { toValue: 8, duration: 50, useNativeDriver: true }),
                Animated.timing(errorShake, { toValue: -8, duration: 50, useNativeDriver: true }),
                Animated.timing(errorShake, { toValue: 0, duration: 50, useNativeDriver: true })
            ]).start();
            return;
        }
        if (notYetActive) {
            // Send her to the thing that actually unblocks this.
            router.push("/onboarding");
            return;
        }
        setShowConfirm(true);
    }, [selectedDate, selectedTime, errorShake, notYetActive, router]);

    const handleOpenMaps = useCallback(() => {
        if (!visit) {
            return;
        }
        const encodedAddress = encodeURIComponent(visit.address);

        if (Platform.OS === "ios") {
            Linking.openURL(`maps://app?daddr=${visit.lat},${visit.lng}&q=${encodedAddress}`);
        } else {
            Linking.openURL(
                `https://www.google.com/maps/dir/?api=1&destination=${visit.lat},${visit.lng}&destination_place_id=${encodedAddress}`
            );
        }
    }, [visit]);

    const ctaLabel = useMemo(() => {
        if (notYetActive) {
            return "Finish your application to book";
        }
        if (selectedDate && selectedTime) {
            return `Schedule for ${selectedDate}, ${selectedTime}`;
        }
        return "Select a date and time";
    }, [selectedDate, selectedTime, notYetActive]);

    // Before activation the CTA is always live: it is a route to onboarding,
    // not a booking, so it must not require a date/time selection first.
    const canSchedule = notYetActive || (selectedDate !== null && selectedTime !== null);

    // Until the visit is assigned to her (security plan PR-18, revised
    // 2026-10-06) the API sends the cross street + area pinned at the
    // intersection, or, with none stored, the area + a pin rounded to ~70 m.
    // The street address arrives once the visit is hers, not on request.
    const approximate = visit ? isApproximateLocation(visit) : false;
    const crossStreet = visit ? isCrossStreetLocation(visit) : false;

    // Format duration
    const durationLabel = visit
        ? visit.durationMinutes >= 60
            ? `${Math.floor(visit.durationMinutes / 60)}h ${visit.durationMinutes % 60 > 0 ? `${visit.durationMinutes % 60}m` : ""}`
            : `${visit.durationMinutes}m`
        : "--";

    if (loading || !visit) {
        return (
            <View style={[styles.container, { justifyContent: "center", alignItems: "center" }]}>
                <ActivityIndicator size="large" color={colors.teal400} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <SafeAreaView edges={["top"]} style={styles.headerSafe}>
                <View style={styles.header}>
                    <TouchableOpacity
                        onPress={() => router.back()}
                        hitSlop={12}
                        style={styles.backButton}
                    >
                        <MaterialCommunityIcons
                            name="arrow-left"
                            size={22}
                            color={colors.textPrimary}
                        />
                    </TouchableOpacity>
                    <Text style={styles.headerTitle}>Visit Info</Text>
                    <View style={{ width: 22 }} />
                </View>
            </SafeAreaView>

            <ScrollView
                ref={scrollRef}
                style={styles.scroll}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                {/* ── Hero Card ── */}
                <View style={styles.heroCard}>
                    {/* Gradient accent strip */}
                    <LinearGradient
                        colors={[...gradients.brand]}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.heroAccent}
                    />

                    <View style={styles.heroContent}>
                        {/* Avatar + Info row */}
                        <View style={styles.heroRow}>
                            <LinearGradient
                                colors={[...gradients.brandSoft]}
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={styles.avatar}
                            >
                                <Text style={styles.avatarText}>{visit.patientInitials}</Text>
                            </LinearGradient>

                            <View style={styles.heroInfo}>
                                {/* Patient name appears once the RN has
                                    requested this visit; until then only
                                    age/gender + initials (in the avatar) show. */}
                                {visit.patientName ? (
                                    <Text style={styles.patientName} numberOfLines={1}>
                                        {visit.patientName}
                                    </Text>
                                ) : null}
                                <Text style={styles.patientDemo}>
                                    {visit.patientAge} yrs / {visit.patientGender}
                                </Text>
                                <View
                                    style={[styles.visitTypeChip, { backgroundColor: vtColor.bg }]}
                                >
                                    <Text
                                        style={[styles.visitTypeChipText, { color: vtColor.text }]}
                                    >
                                        {visit.visitTypeDisplayName ??
                                            VISIT_TYPE_LABELS[visit.visitType] ??
                                            visit.visitType}
                                    </Text>
                                </View>
                            </View>

                            {/* Payment */}
                            <View style={styles.paymentBlock}>
                                <Text style={styles.paymentAmount}>${visit.payment}</Text>
                                <View style={styles.paymentIcon}>
                                    <MaterialCommunityIcons
                                        name="currency-usd"
                                        size={14}
                                        color={colors.teal500}
                                    />
                                </View>
                            </View>
                        </View>
                    </View>
                </View>

                {/* ── Info Chips 2x2 Grid ── */}
                <View style={styles.infoGrid}>
                    <View style={styles.infoChip}>
                        <MaterialCommunityIcons
                            name="map-marker-outline"
                            size={18}
                            color={colors.teal400}
                        />
                        <Text style={styles.infoChipLabel}>{locationChipLabel(visit)}</Text>
                        <Text style={styles.infoChipValue} numberOfLines={2}>
                            {visitLocationLine(visit)}
                        </Text>
                    </View>
                    <View style={styles.infoChip}>
                        <MaterialCommunityIcons
                            name="timer-outline"
                            size={18}
                            color={colors.teal400}
                        />
                        <Text style={styles.infoChipLabel}>DURATION</Text>
                        <Text style={styles.infoChipValue}>{durationLabel}</Text>
                    </View>
                    {visit.patientLanguage ? (
                        <View style={styles.infoChip}>
                            <MaterialCommunityIcons
                                name="translate"
                                size={18}
                                color={colors.teal400}
                            />
                            <Text style={styles.infoChipLabel}>LANGUAGE</Text>
                            <Text
                                style={[styles.infoChipValue, styles.infoChipValueCapitalize]}
                                numberOfLines={1}
                            >
                                {visit.patientLanguage}
                            </Text>
                        </View>
                    ) : null}
                </View>

                {/* ── Map Card ── */}
                <View style={styles.mapCard}>
                    <View style={styles.mapArea}>
                        <Image
                            {...logRocketRedactProps()}
                            source={{
                                uri: approximate
                                    ? approximateStaticMapUrl(
                                          visit.lat,
                                          visit.lng,
                                          process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY,
                                          { fill: colors.teal400, stroke: colors.teal600 }
                                      )
                                    : crossStreet
                                      ? crossStreetStaticMapUrl(
                                            visit.lat,
                                            visit.lng,
                                            process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY,
                                            colors.teal600
                                        )
                                      : `https://maps.googleapis.com/maps/api/staticmap?center=${visit.lat},${visit.lng}&zoom=15&size=600x360&scale=2&maptype=roadmap&markers=color:0x0B2D4D%7C${visit.lat},${visit.lng}&style=feature:poi%7Cvisibility:off&key=${process.env.EXPO_PUBLIC_GOOGLE_MAPS_API_KEY}`
                            }}
                            style={StyleSheet.absoluteFill}
                            resizeMode="cover"
                        />

                        {/* Directions to a pin rounded to 3 decimals would route
                            her to a door up to ~70 m from the patient's, so an
                            approximate location says what it is instead
                            (PR-18). A cross street's pin IS the corner, so
                            directions go there. */}
                        {!allowsDirections(visit) ? (
                            <View style={styles.openMapsButton}>
                                <MaterialCommunityIcons
                                    name="map-marker-radius-outline"
                                    size={16}
                                    color={colors.teal600}
                                />
                                <Text style={styles.approxAreaText}>Approximate area</Text>
                            </View>
                        ) : (
                            <TouchableOpacity
                                activeOpacity={0.8}
                                onPress={handleOpenMaps}
                                style={styles.openMapsButton}
                            >
                                <MaterialCommunityIcons
                                    name="directions"
                                    size={16}
                                    color={colors.teal500}
                                />
                                <Text style={styles.openMapsText}>Get Directions</Text>
                            </TouchableOpacity>
                        )}
                    </View>

                    {/* Address, or the cross street / area until the visit is hers */}
                    <View style={styles.addressSection}>
                        <MaterialCommunityIcons
                            name="map-marker"
                            size={16}
                            color={colors.teal400}
                        />
                        <View style={styles.addressBody}>
                            <Text style={styles.addressText}>{visitLocationLine(visit)}</Text>
                            {showsExactAddressHint(visit) ? (
                                <Text style={styles.addressHint}>
                                    {EXACT_ADDRESS_ONCE_YOURS_TEXT}
                                </Text>
                            ) : null}
                        </View>
                    </View>

                    {/* Distance + Duration pills */}
                    <View style={styles.travelRow}>
                        <View style={styles.travelPill}>
                            <MaterialCommunityIcons
                                name="map-marker-distance"
                                size={15}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.travelText}>{distanceAwayText(visit)}</Text>
                        </View>
                        <View style={styles.travelDot} />
                        <View style={styles.travelPill}>
                            <MaterialCommunityIcons
                                name="timer-outline"
                                size={15}
                                color={colors.textSecondary}
                            />
                            <Text style={styles.travelText}>{visit.durationMinutes} min</Text>
                        </View>
                    </View>
                </View>

                {/* ── Time Slot Picker ── */}
                <Animated.View
                    style={[
                        styles.section,
                        showError && styles.sectionError,
                        { transform: [{ translateX: errorShake }] }
                    ]}
                    onLayout={(e) => {
                        timeSlotY.current = e.nativeEvent.layout.y;
                    }}
                >
                    {showError && (
                        <View style={styles.errorBanner}>
                            <MaterialCommunityIcons
                                name="alert-circle"
                                size={16}
                                color={colors.error500}
                            />
                            <Text style={styles.errorBannerText}>
                                {!selectedDate
                                    ? "Please select a date to continue"
                                    : "Please select a start time to continue"}
                            </Text>
                        </View>
                    )}
                    <Text style={styles.sectionTitle}>Select time slot</Text>
                    <Text style={styles.sectionDesc}>
                        Once you select the date and time, we will contact the patient to get a
                        confirmation
                    </Text>

                    {/* Date selector */}
                    <View style={styles.dateRow}>
                        <Text style={styles.dateRowLabel}>Select date</Text>
                        <MaterialCommunityIcons
                            name="arrow-right"
                            size={18}
                            color={colors.textTertiary}
                        />
                    </View>

                    <ScrollView
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        contentContainerStyle={styles.dateScroll}
                    >
                        {visit.availableDates.map((dateSlot) => {
                            const isSelected = selectedDate === dateSlot.date;
                            return (
                                <TouchableOpacity
                                    key={dateSlot.date}
                                    activeOpacity={0.7}
                                    onPress={() => handleDateSelect(dateSlot.date)}
                                    style={[styles.dateCard, isSelected && styles.dateCardSelected]}
                                >
                                    {isSelected ? (
                                        <LinearGradient
                                            colors={[...gradients.brand]}
                                            start={{ x: 0, y: 0 }}
                                            end={{ x: 1, y: 1 }}
                                            style={[
                                                StyleSheet.absoluteFill,
                                                { borderRadius: radius.lg }
                                            ]}
                                        />
                                    ) : null}
                                    <Text
                                        style={[
                                            styles.dateCardDate,
                                            isSelected && styles.dateCardDateSelected
                                        ]}
                                    >
                                        {dateSlot.date}
                                    </Text>
                                    <Text
                                        style={[
                                            styles.dateCardDay,
                                            isSelected && styles.dateCardDaySelected
                                        ]}
                                    >
                                        {dateSlot.dayName}
                                    </Text>
                                    <Text
                                        style={[
                                            styles.dateCardWindow,
                                            isSelected && styles.dateCardWindowSelected
                                        ]}
                                    >
                                        {dateSlot.window}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </ScrollView>

                    {/* Time grid — shows after date is selected */}
                    {selectedDateSlot && (
                        <View style={styles.timeSection}>
                            <Text style={styles.timeLabel}>Select start time</Text>
                            <View style={styles.timeGrid}>
                                {selectedDateSlot.timeSlots.map((time) => {
                                    const isSelected = selectedTime === time;
                                    // Greyed out + unpickable when it collides with
                                    // (or falls within 15 min of) something already
                                    // on her schedule for this day.
                                    const isBusy = selectedDateSlot.unavailableTimes.includes(time);
                                    return (
                                        <TouchableOpacity
                                            key={time}
                                            activeOpacity={0.7}
                                            disabled={isBusy}
                                            onPress={() => handleTimeSelect(time)}
                                            style={[
                                                styles.timeChip,
                                                isSelected && styles.timeChipSelected,
                                                isBusy && styles.timeChipDisabled
                                            ]}
                                        >
                                            <Text
                                                style={[
                                                    styles.timeChipText,
                                                    isSelected && styles.timeChipTextSelected,
                                                    isBusy && styles.timeChipTextDisabled
                                                ]}
                                            >
                                                {time}
                                            </Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        </View>
                    )}

                    {/* Her own schedule for the selected day — confirmed visits
                        and other pending requests — so she can pick a time that
                        fits. Conflicting start times above are greyed out; this
                        strip shows what they collide with. */}
                    {selectedDateSlot && (
                        <View style={styles.scheduleCard}>
                            <View style={styles.scheduleHeader}>
                                <MaterialCommunityIcons
                                    name="calendar-clock"
                                    size={16}
                                    color={colors.textSecondary}
                                />
                                <Text style={styles.scheduleTitle}>
                                    Your schedule for {selectedDateSlot.date}
                                </Text>
                            </View>
                            {selectedDateSlot.schedule.length === 0 ? (
                                <Text style={styles.scheduleEmpty}>
                                    You have nothing else booked this day.
                                </Text>
                            ) : (
                                selectedDateSlot.schedule.map((item, idx) => (
                                    <View
                                        key={`${item.startLabel}-${idx}`}
                                        style={styles.scheduleRow}
                                    >
                                        <View
                                            style={[
                                                styles.scheduleDot,
                                                {
                                                    backgroundColor:
                                                        item.kind === "pending"
                                                            ? colors.warning500
                                                            : colors.info500
                                                }
                                            ]}
                                        />
                                        <View style={styles.scheduleTimeCol}>
                                            <Text style={styles.scheduleTime}>
                                                {item.startLabel}
                                            </Text>
                                            <Text style={styles.scheduleTimeEnd}>
                                                {item.endLabel}
                                            </Text>
                                        </View>
                                        <View style={styles.scheduleInfo}>
                                            <Text style={styles.scheduleName} numberOfLines={1}>
                                                {item.patientName}
                                            </Text>
                                            <Text style={styles.scheduleType} numberOfLines={1}>
                                                {item.visitType ?? "Visit"}
                                                {item.kind === "pending" ? " · Pending" : ""}
                                            </Text>
                                        </View>
                                    </View>
                                ))
                            )}
                        </View>
                    )}
                </Animated.View>

                {/* Bottom spacing for CTA */}
                <View style={{ height: 120 }} />
            </ScrollView>

            {/* ── Sticky Bottom ── */}
            <SafeAreaView edges={["bottom"]} style={styles.ctaWrapper}>
                {visit.request ? (
                    <>
                        {/* Request pending info */}
                        <View style={styles.requestInfoCard}>
                            <MaterialCommunityIcons
                                name="clock-check-outline"
                                size={20}
                                color={colors.warning600}
                            />
                            <View style={{ flex: 1, marginLeft: spacing.sm }}>
                                <Text style={styles.requestInfoTitle}>Request pending</Text>
                                <Text style={styles.requestInfoSub}>
                                    Proposed: {visit.request.proposedDate} at{" "}
                                    {visit.request.proposedTime}
                                </Text>
                            </View>
                        </View>
                        <TouchableOpacity
                            activeOpacity={0.7}
                            onPress={() => {
                                const msg = encodeURIComponent(
                                    `I would like to cancel my visit request for visit #${visit.id} (proposed ${visit.request?.proposedDate} at ${visit.request?.proposedTime}).`
                                );
                                router.push(`/tickets/new?prefill=${msg}&visitId=${visit.id}`);
                            }}
                            style={styles.cancelRequestBtn}
                        >
                            <Text style={styles.cancelRequestText}>Cancel Request</Text>
                        </TouchableOpacity>
                    </>
                ) : (
                    <>
                        <TouchableOpacity activeOpacity={0.8} onPress={handleSchedulePress}>
                            <LinearGradient
                                colors={
                                    canSchedule
                                        ? [...gradients.teal]
                                        : [colors.gray200, colors.gray300]
                                }
                                start={{ x: 0, y: 0 }}
                                end={{ x: 1, y: 1 }}
                                style={styles.acceptBtn}
                            >
                                <Text
                                    style={[
                                        styles.acceptText,
                                        !canSchedule && styles.acceptTextDisabled
                                    ]}
                                >
                                    {ctaLabel}
                                </Text>
                                {canSchedule && (
                                    <View style={styles.ctaArrow}>
                                        <MaterialCommunityIcons
                                            name="arrow-right"
                                            size={16}
                                            color={colors.teal400}
                                        />
                                    </View>
                                )}
                            </LinearGradient>
                        </TouchableOpacity>

                        <TouchableOpacity
                            activeOpacity={0.7}
                            onPress={() => router.back()}
                            style={styles.declineBtn}
                        >
                            <Text style={styles.declineText}>Decline</Text>
                        </TouchableOpacity>
                    </>
                )}
            </SafeAreaView>

            {/* ── Confirmation Modal ── */}
            {showConfirm && (
                <View style={styles.confirmOverlay}>
                    <SafeAreaView edges={["top", "bottom"]} style={styles.confirmSafe}>
                        {/* Hero Header */}
                        <LinearGradient
                            colors={[...gradients.alive]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 1 }}
                            style={styles.confirmHero}
                        >
                            {/* Decorative circles */}
                            <View style={styles.confirmHeroCircle1} />
                            <View style={styles.confirmHeroCircle2} />

                            <TouchableOpacity
                                onPress={() => setShowConfirm(false)}
                                hitSlop={12}
                                style={styles.confirmBack}
                            >
                                <MaterialCommunityIcons
                                    name="arrow-left"
                                    size={22}
                                    color="rgba(255,255,255,0.7)"
                                />
                            </TouchableOpacity>

                            <View style={styles.confirmHeroContent}>
                                <View style={styles.confirmIconCircle}>
                                    <MaterialCommunityIcons
                                        name="calendar-check"
                                        size={26}
                                        color={colors.teal400}
                                    />
                                </View>
                                <Text style={styles.confirmDateTime}>
                                    {selectedDateSlot?.dayName}, {selectedDate}
                                </Text>
                                <Text style={styles.confirmTime}>{selectedTime}</Text>
                                <View style={styles.confirmPills}>
                                    <View style={styles.confirmPill}>
                                        <Text style={styles.confirmPillText}>
                                            {visit.patientInitials}
                                        </Text>
                                    </View>
                                    <View style={styles.confirmPill}>
                                        <Text style={styles.confirmPillText}>
                                            {visit.visitTypeDisplayName ??
                                                VISIT_TYPE_LABELS[visit.visitType] ??
                                                visit.visitType}
                                        </Text>
                                    </View>
                                    <View style={styles.confirmPill}>
                                        <Text style={styles.confirmPillText}>${visit.payment}</Text>
                                    </View>
                                </View>
                            </View>
                        </LinearGradient>

                        <ScrollView
                            contentContainerStyle={styles.confirmContent}
                            showsVerticalScrollIndicator={false}
                        >
                            {/* What happens next */}
                            <View style={styles.confirmCard}>
                                <Text style={styles.confirmCardTitle}>What happens next</Text>
                                <Text style={styles.confirmCardBody}>
                                    We'll call the patient to confirm your proposed time. You'll get
                                    a notification when they confirm, decline, or ask to reschedule.
                                </Text>

                                <View style={styles.confirmDivider} />

                                <View style={styles.confirmLockRow}>
                                    <MaterialCommunityIcons
                                        name="lock-outline"
                                        size={18}
                                        color={colors.textPrimary}
                                    />
                                    <Text style={styles.confirmLockText}>
                                        You can't change this date or time once you commit.
                                    </Text>
                                </View>
                            </View>

                            {/* Info callout */}
                            <View style={styles.confirmInfoCard}>
                                <LinearGradient
                                    colors={[colors.teal400, colors.teal500]}
                                    style={styles.confirmCalloutIcon}
                                >
                                    <MaterialCommunityIcons
                                        name="clock-alert-outline"
                                        size={16}
                                        color="#FFFFFF"
                                    />
                                </LinearGradient>
                                <View style={styles.confirmCalloutBody}>
                                    <Text style={styles.confirmCalloutTitle}>
                                        Auto-cancel policy
                                    </Text>
                                    <Text style={styles.confirmCalloutText}>
                                        If the patient doesn't confirm within 1 hour before the
                                        visit start time, the visit will be canceled automatically.
                                    </Text>
                                </View>
                            </View>

                            {/* Warning callout */}
                            <View style={styles.confirmWarningCard}>
                                <LinearGradient
                                    colors={[colors.warning500, colors.warning600]}
                                    style={styles.confirmCalloutIcon}
                                >
                                    <MaterialCommunityIcons
                                        name="alert-outline"
                                        size={16}
                                        color="#FFFFFF"
                                    />
                                </LinearGradient>
                                <View style={styles.confirmCalloutBody}>
                                    <Text style={styles.confirmCalloutTitle}>
                                        Reliability score
                                    </Text>
                                    <Text style={styles.confirmCalloutText}>
                                        Cancellations after committing reduce your reliability score
                                        and may affect future visit availability.
                                    </Text>
                                </View>
                            </View>
                        </ScrollView>

                        {/* Commit Button */}
                        <View style={styles.confirmCtaWrapper}>
                            <TouchableOpacity
                                activeOpacity={0.8}
                                disabled={submitting}
                                onPress={async () => {
                                    if (
                                        !visit ||
                                        !selectedDate ||
                                        !selectedTime ||
                                        !selectedDateSlot
                                    ) {
                                        return;
                                    }
                                    setSubmitting(true);
                                    try {
                                        // Backend supplies ISO date directly — don't reparse the
                                        // "Mar 19" display string via new Date(): Hermes returns
                                        // Invalid Date for non-ISO inputs, which produced
                                        // "NaN-NaN-NaN" and a 400 from the request endpoint.
                                        const proposedDate = selectedDateSlot.isoDate;
                                        // Parse "9:30 AM" → "09:30"
                                        const match = selectedTime.match(
                                            /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
                                        );
                                        let h = Number(match?.[1] ?? 0);
                                        const m = match?.[2] ?? "00";
                                        const ampm = match?.[3]?.toUpperCase();
                                        if (ampm === "PM" && h !== 12) {
                                            h += 12;
                                        }
                                        if (ampm === "AM" && h === 12) {
                                            h = 0;
                                        }
                                        const proposedTime = `${String(h).padStart(2, "0")}:${m}`;

                                        await requestVisit(visit.id, proposedDate, proposedTime);
                                        showNotification(
                                            "Visit request submitted! We'll notify you when the patient confirms.",
                                            "approved"
                                        );
                                        setShowConfirm(false);
                                        router.back();
                                    } catch (e) {
                                        // Visit-date compliance gate. The server returns a
                                        // structured 409 listing every item that would not be
                                        // valid on the visit date; render it in a popup
                                        // instead of the generic failure notification so the
                                        // nurse can see exactly what to renew.
                                        const blocked = extractVisitComplianceBlockers(e);
                                        if (blocked) {
                                            setComplianceBlocked(blocked);
                                            setShowConfirm(false);
                                            // Stay on the screen — closing the popup leaves
                                            // the nurse on the visit detail, not navigated
                                            // away. They can re-request later after renewing.
                                        } else {
                                            showNotification(
                                                e instanceof Error
                                                    ? e.message
                                                    : "Failed to submit request",
                                                "failed"
                                            );
                                            setShowConfirm(false);
                                            router.back();
                                        }
                                    } finally {
                                        setSubmitting(false);
                                    }
                                }}
                            >
                                <LinearGradient
                                    colors={[...gradients.brand]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 1 }}
                                    style={styles.commitBtn}
                                >
                                    <Text style={styles.commitText}>
                                        {submitting ? "Submitting..." : "Commit & Send Request"}
                                    </Text>
                                    <View style={styles.ctaArrow}>
                                        <MaterialCommunityIcons
                                            name="check"
                                            size={16}
                                            color={colors.teal400}
                                        />
                                    </View>
                                </LinearGradient>
                            </TouchableOpacity>
                        </View>
                    </SafeAreaView>
                </View>
            )}

            {/*
             * Visit-date compliance gate popup. Mounted at the screen root
             * so it overlays the entire detail view including the confirm
             * sheet (which is itself dismissed before this opens — see
             * the catch handler above).
             */}
            <VisitComplianceBlockerModal
                visible={complianceBlocked !== null}
                blocked={complianceBlocked}
                onClose={() => setComplianceBlocked(null)}
            />
        </View>
    );
}

// ===============================================
// Styles
// ===============================================

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },

    // ── Header ──
    headerSafe: {
        backgroundColor: colors.surface,
        zIndex: 10
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.lg,
        paddingVertical: spacing.md,
        borderBottomWidth: 1.5,
        borderBottomColor: colors.border
    },
    backButton: {
        padding: spacing.xs
    },
    headerTitle: {
        fontSize: 17,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },

    // ── Scroll ──
    scroll: {
        flex: 1
    },
    scrollContent: {
        paddingTop: spacing.base
    },

    // ── Hero Card ──
    heroCard: {
        marginHorizontal: spacing.base,
        marginBottom: spacing.base,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        overflow: "hidden",
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.sm
    },
    heroAccent: {
        height: 3
    },
    heroContent: {
        padding: spacing.base
    },
    heroRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md
    },
    avatar: {
        width: 52,
        height: 52,
        borderRadius: radius.xl,
        alignItems: "center",
        justifyContent: "center"
    },
    avatarText: {
        fontSize: 18,
        fontFamily: fonts.extraBold,
        color: colors.teal600
    },
    heroInfo: {
        flex: 1,
        gap: spacing.xs
    },
    patientName: {
        ...typography.h2,
        color: colors.textPrimary
    },
    patientDemo: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    visitTypeChip: {
        alignSelf: "flex-start",
        paddingHorizontal: spacing.sm,
        paddingVertical: 2,
        borderRadius: radius.full
    },
    visitTypeChipText: {
        ...typography.micro,
        letterSpacing: 0.3
    },
    paymentBlock: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs
    },
    paymentAmount: {
        fontSize: 28,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    paymentIcon: {
        width: 24,
        height: 24,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: colors.teal200,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },

    // ── Info Grid 2x2 ──
    infoGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        marginHorizontal: spacing.base,
        marginBottom: spacing.base,
        gap: spacing.sm
    },
    infoChip: {
        width: "48.5%",
        backgroundColor: colors.surface,
        borderRadius: 16,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.md,
        gap: spacing.xs
    },
    infoChipLabel: {
        ...typography.micro,
        color: colors.textTertiary,
        letterSpacing: 1
    },
    infoChipValue: {
        ...typography.caption,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    infoChipValueCapitalize: {
        textTransform: "capitalize"
    },

    // ── Map Card ──
    mapCard: {
        marginHorizontal: spacing.base,
        marginBottom: spacing.lg,
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        overflow: "hidden",
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.sm
    },
    mapArea: {
        height: 180,
        justifyContent: "center",
        alignItems: "center",
        position: "relative"
    },
    openMapsButton: {
        position: "absolute",
        bottom: 10,
        right: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        backgroundColor: "rgba(255,255,255,0.95)",
        paddingHorizontal: spacing.md,
        paddingVertical: 7,
        borderRadius: radius.full,
        ...shadows.sm
    },
    openMapsText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.teal500
    },
    addressSection: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.xs,
        paddingHorizontal: spacing.base,
        paddingTop: spacing.md,
        paddingBottom: spacing.sm
    },
    addressBody: {
        flex: 1
    },
    addressText: {
        ...typography.body,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    addressHint: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 2
    },
    approxAreaText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.teal600
    },
    travelRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.md,
        paddingVertical: spacing.md,
        marginHorizontal: spacing.base,
        backgroundColor: colors.gray50,
        borderRadius: radius.md,
        marginBottom: spacing.base
    },
    travelPill: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs
    },
    travelText: {
        ...typography.caption,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    travelDot: {
        width: 4,
        height: 4,
        borderRadius: 2,
        backgroundColor: colors.gray300
    },

    // ── Section ──
    section: {
        marginHorizontal: spacing.base,
        marginBottom: spacing.lg,
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        padding: spacing.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.xs
    },
    sectionError: {
        borderColor: colors.error400,
        borderWidth: 2
    },
    errorBanner: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.error50,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.sm,
        borderRadius: radius.sm,
        marginBottom: spacing.md
    },
    errorBannerText: {
        ...typography.bodySmall,
        fontFamily: fonts.semiBold,
        color: colors.error500,
        flex: 1
    },
    sectionTitle: {
        ...typography.h2,
        color: colors.textPrimary,
        marginBottom: spacing.sm
    },
    sectionDesc: {
        ...typography.body,
        color: colors.textTertiary,
        lineHeight: 20,
        marginBottom: spacing.lg
    },

    // ── Date Selector ──
    dateRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: spacing.md
    },
    dateRowLabel: {
        fontSize: 15,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    dateScroll: {
        gap: spacing.md,
        paddingBottom: spacing.sm
    },
    dateCard: {
        width: 150,
        padding: spacing.base,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: colors.surface,
        overflow: "hidden"
    },
    dateCardSelected: {
        borderColor: "transparent"
    },
    dateCardDate: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    dateCardDateSelected: {
        color: "#FFFFFF"
    },
    dateCardDay: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 2
    },
    dateCardDaySelected: {
        color: "rgba(255,255,255,0.8)"
    },
    dateCardWindow: {
        ...typography.caption,
        color: colors.teal500,
        fontFamily: fonts.semiBold,
        marginTop: spacing.xs
    },
    dateCardWindowSelected: {
        color: "rgba(255,255,255,0.7)"
    },

    // ── Time Grid ──
    timeSection: {
        marginTop: spacing.lg,
        paddingTop: spacing.lg,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    scheduleCard: {
        marginTop: spacing.lg,
        padding: spacing.base,
        borderRadius: radius.md,
        backgroundColor: colors.gray100,
        borderWidth: 1,
        borderColor: colors.border,
        gap: spacing.sm
    },
    scheduleHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6
    },
    scheduleTitle: {
        flex: 1,
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    scheduleEmpty: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    scheduleRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm
    },
    scheduleDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        marginTop: 4
    },
    scheduleTimeCol: {
        width: 72
    },
    scheduleTime: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    scheduleTimeEnd: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    scheduleInfo: {
        flex: 1,
        gap: 2
    },
    scheduleName: {
        fontSize: 12,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    scheduleType: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    timeLabel: {
        fontSize: 15,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary,
        marginBottom: spacing.md
    },
    timeGrid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.sm
    },
    timeChip: {
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: radius.md,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: colors.surface
    },
    timeChipSelected: {
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    timeChipText: {
        ...typography.bodySmall,
        fontFamily: fonts.semiBold,
        color: colors.textPrimary
    },
    timeChipTextSelected: {
        color: colors.teal500
    },
    timeChipDisabled: {
        backgroundColor: colors.gray100,
        borderColor: colors.gray200
    },
    timeChipTextDisabled: {
        color: colors.gray400,
        textDecorationLine: "line-through"
    },

    // ── Required Documents ──
    docsList: {
        marginTop: spacing.sm
    },
    docItem: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: 14
    },
    docItemBorder: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    docText: {
        ...typography.body,
        fontFamily: fonts.medium,
        color: colors.textPrimary,
        flex: 1
    },

    // ── Sticky CTA ──
    ctaWrapper: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(250,251,255,0.95)",
        paddingHorizontal: spacing.base,
        paddingTop: spacing.md,
        borderTopWidth: 1.5,
        borderTopColor: colors.border
    },
    acceptBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        paddingVertical: 16,
        borderRadius: radius.md,
        ...shadows.teal
    },
    acceptText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    acceptTextDisabled: {
        color: colors.textTertiary
    },
    declineBtn: {
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: spacing.md,
        marginTop: spacing.sm,
        borderRadius: radius.md,
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: colors.surface
    },
    declineText: {
        ...typography.body,
        fontFamily: fonts.semiBold,
        color: colors.textTertiary
    },
    requestInfoCard: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: colors.warning50,
        borderRadius: radius.md,
        padding: spacing.md,
        marginBottom: spacing.sm,
        borderWidth: 1,
        borderColor: colors.warning400
    },
    requestInfoTitle: {
        fontFamily: fonts.bold,
        fontSize: 14,
        color: colors.textPrimary
    },
    requestInfoSub: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 2
    },
    cancelRequestBtn: {
        alignItems: "center",
        justifyContent: "center",
        paddingVertical: spacing.md,
        borderRadius: radius.md,
        borderWidth: 1.5,
        borderColor: colors.error400,
        backgroundColor: colors.surface
    },
    cancelRequestText: {
        ...typography.body,
        fontFamily: fonts.semiBold,
        color: colors.error500
    },
    ctaArrow: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: "rgba(255,255,255,0.25)",
        alignItems: "center",
        justifyContent: "center"
    },

    // ── Commit Button (Confirmation Modal) ──
    commitBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        paddingVertical: 16,
        borderRadius: radius.md,
        ...shadows.brand
    },
    commitText: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },

    // ── Confirmation Modal ──
    confirmOverlay: {
        ...StyleSheet.absoluteFill,
        backgroundColor: colors.background,
        zIndex: 100
    },
    confirmSafe: {
        flex: 1
    },
    confirmHero: {
        paddingTop: spacing.base,
        paddingBottom: spacing.xl,
        paddingHorizontal: spacing.lg,
        position: "relative",
        overflow: "hidden"
    },
    confirmHeroCircle1: {
        position: "absolute",
        right: -30,
        top: -30,
        width: 120,
        height: 120,
        borderRadius: 60,
        backgroundColor: "rgba(69,210,176,0.1)"
    },
    confirmHeroCircle2: {
        position: "absolute",
        left: "40%",
        bottom: -40,
        width: 80,
        height: 80,
        borderRadius: 40,
        backgroundColor: "rgba(231,72,245,0.06)"
    },
    confirmBack: {
        marginBottom: spacing.lg
    },
    confirmHeroContent: {
        alignItems: "center"
    },
    confirmIconCircle: {
        width: 52,
        height: 52,
        borderRadius: 26,
        backgroundColor: "rgba(255,255,255,0.15)",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: spacing.md
    },
    confirmDateTime: {
        fontSize: 16,
        fontFamily: fonts.semiBold,
        color: "rgba(255,255,255,0.7)"
    },
    confirmTime: {
        fontSize: 28,
        fontFamily: fonts.extraBold,
        color: "#FFFFFF",
        marginTop: spacing.xs
    },
    confirmPills: {
        flexDirection: "row",
        gap: spacing.sm,
        marginTop: spacing.md
    },
    confirmPill: {
        backgroundColor: "rgba(255,255,255,0.15)",
        paddingHorizontal: 12,
        paddingVertical: spacing.xs,
        borderRadius: radius.full
    },
    confirmPillText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "rgba(255,255,255,0.8)"
    },
    confirmContent: {
        padding: spacing.lg,
        paddingBottom: 120
    },
    confirmCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        padding: spacing.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        marginBottom: spacing.base,
        ...shadows.xs
    },
    confirmCardTitle: {
        ...typography.title,
        color: colors.textPrimary,
        marginBottom: spacing.sm
    },
    confirmCardBody: {
        ...typography.body,
        color: colors.textSecondary,
        lineHeight: 21
    },
    confirmDivider: {
        height: 1,
        backgroundColor: colors.border,
        marginVertical: spacing.md
    },
    confirmLockRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    confirmLockText: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        flex: 1
    },
    confirmInfoCard: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        padding: spacing.base,
        borderWidth: 1.5,
        borderColor: colors.border,
        marginBottom: spacing.sm,
        ...shadows.xs
    },
    confirmCalloutIcon: {
        width: 32,
        height: 32,
        borderRadius: radius.sm,
        alignItems: "center",
        justifyContent: "center",
        marginTop: 2
    },
    confirmCalloutBody: {
        flex: 1
    },
    confirmCalloutTitle: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: 2
    },
    confirmCalloutText: {
        ...typography.caption,
        color: colors.textSecondary,
        lineHeight: 19
    },
    confirmWarningCard: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        padding: spacing.base,
        borderWidth: 1.5,
        borderColor: colors.border,
        ...shadows.xs
    },
    confirmCtaWrapper: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: "rgba(250,251,255,0.95)",
        paddingHorizontal: spacing.base,
        paddingTop: spacing.md,
        paddingBottom: spacing.xl,
        borderTopWidth: 1.5,
        borderTopColor: colors.border
    }
});
